// The Ask Hopscotch panel: header, body, context pill, thread, and composer.
// CompanionRoot owns the responsive width; this component owns the panel UI.

import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useGlobalState } from '@/stores/globalState'
import { useCompanionContext } from '@/lib/chatContext'
import { fetchGuideById } from '@/lib/api/guides'
import { useGameTemplates } from '@/hooks/useGameTemplates'
import { getGeneralPrompts, getSetPrompts, type PromptGame } from '@/lib/companionPrompts'
import { AskMark } from './AskMark'
import { AssistantMsg, UserMsg } from './MessageBubbles'
import { useCompanionChat } from './useCompanionChat'
import './styles/CompanionPanel.css'

const CHAR_LIMIT = 2000

function titleCase(id: string): string {
  return id.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export function CompanionPanel({ onClose }: { onClose: () => void }) {
  const chat = useCompanionChat()
  const context = useCompanionContext()
  const activeLearner = useGlobalState((s) => s.activeLearner)
  const setCompanionContextDetached = useGlobalState((s) => s.setCompanionContextDetached)

  const { data: guide } = useQuery({
    queryKey: ['reading-guide', context.guideId],
    queryFn: () => fetchGuideById(context.guideId!),
    enabled: !!context.guideId,
  })
  const guideTitle = context.guideId ? guide?.title ?? 'this set' : null

  const { data: templateList } = useGameTemplates()
  const templateName = (mechanicId: string) =>
    templateList?.find((t) => t.mechanic_id === mechanicId)?.name
  const promptGames: PromptGame[] = context.guideId
    ? guide?.gameRows?.map((g) => ({
        name: templateName(g.mechanicId) ?? g.layerName?.trim() ?? titleCase(g.mechanicId),
      })) ??
      guide?.games?.map((g) => ({ name: g.name })) ??
      []
    : []

  const learnerName = activeLearner?.name ?? null
  const prompts = context.guideId
    ? getSetPrompts(learnerName, promptGames)
    : getGeneralPrompts(learnerName)

  const visibleMessages = chat.messages.filter((m) => m.role !== 'agent_status')
  const isEmpty = visibleMessages.length === 0

  const [inputValue, setInputValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [visibleMessages.length, chat.isStreaming])

  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 100)}px`
  }, [inputValue])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const submit = (text?: string) => {
    const trimmed = (text ?? inputValue).trim()
    if (!trimmed || chat.isStreaming) return
    chat.send(trimmed)
    setInputValue('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
  }

  return (
    <div className="hs-brand companion-panel">
      <div className="companion-panel__header">
        <AskMark size={30} />
        <div className="companion-panel__heading">
          <div className="companion-panel__title">Ask Hopscotch</div>
          <div className="companion-panel__subtitle">Your reading companion</div>
        </div>
        {!isEmpty && (
          <button
            type="button"
            onClick={chat.clear}
            title="Clear this conversation"
            aria-label="Clear this conversation"
            className="hs-press hs-press--paper companion-panel__icon-button"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 20 20"
              className="companion-panel__svg companion-panel__clear-icon"
            >
              <path d="M4 6h12M8.5 6V4.5h3V6M6 6l.8 10h6.4L14 6M8.5 9v4.5M11.5 9v4.5" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Collapse"
          aria-label="Collapse Ask Hopscotch"
          className="hs-press hs-press--paper companion-panel__icon-button"
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 20 20"
            className="companion-panel__svg companion-panel__collapse-icon"
          >
            <path d="M8 4l6 6-6 6" />
          </svg>
        </button>
      </div>

      <div className="companion-panel__body">
        {context.guideId && (
          <div className="companion-panel__context-pill">
            <svg
              width="15"
              height="15"
              viewBox="0 0 20 20"
              className="companion-panel__svg companion-panel__context-icon"
            >
              <path d="M10 5C8 3.5 5 3.5 3 4v12c2-.5 5-.5 7 1 2-1.5 5-1.5 7-1V4c-2-.5-5-.5-7 1zM10 5v13" />
            </svg>
            <span className="companion-panel__context-copy">
              Asking about <strong className="companion-panel__strong">{guideTitle}</strong>
            </span>
            <button
              type="button"
              onClick={() => setCompanionContextDetached(true)}
              aria-label="Ask about something else"
              title="Ask about something else"
              className="companion-panel__context-dismiss"
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 20 20"
                className="companion-panel__svg companion-panel__dismiss-icon"
              >
                <path d="M5 5l10 10M15 5L5 15" />
              </svg>
            </button>
          </div>
        )}

        {isEmpty ? (
          <>
            <div className="companion-panel__empty-copy">
              <div className="companion-panel__empty-title">
                {context.guideId ? (
                  <>
                    Questions about
                    <br />
                    this set?
                  </>
                ) : (
                  <>
                    Hi! What can I help
                    <br />
                    you with today?
                  </>
                )}
              </div>
              <div className="companion-panel__empty-description">
                Ask me anything about {context.guideId ? 'this set, ' : ''}the games, reading
                at home, or how {learnerName ?? 'your child'}&rsquo;s doing.
              </div>
            </div>
            <div className="companion-panel__prompt-label">Try asking</div>
            <div className="companion-panel__prompt-list">
              {prompts.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => submit(p)}
                  className="hs-press hs-press--paper companion-panel__prompt-button"
                >
                  <span className="companion-panel__prompt-text">{p}</span>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 20 20"
                    className="companion-panel__svg companion-panel__prompt-icon"
                  >
                    <path d="M7 4l6 6-6 6" />
                  </svg>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="companion-panel__thread">
            {visibleMessages.map((msg) =>
              msg.role === 'user' ? (
                <UserMsg key={msg.id} message={msg} />
              ) : (
                <AssistantMsg key={msg.id} message={msg} onAction={chat.runMessageAction} />
              ),
            )}
          </div>
        )}

        {chat.error && (
          <div className="companion-panel__error">
            <span className="companion-panel__error-text">
              Something went wrong. Please try again.
            </span>
            <button
              type="button"
              onClick={chat.retry}
              className="hs-press hs-press--paper companion-panel__retry-button"
            >
              Retry
            </button>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="companion-panel__composer">
        <div className="companion-panel__composer-box">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => {
              if (e.target.value.length <= CHAR_LIMIT) setInputValue(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                submit()
              }
            }}
            placeholder={`Ask about games, reading, or ${learnerName ?? 'your child'}...`}
            disabled={chat.isStreaming}
            rows={1}
            className="companion-panel__textarea"
          />
          <button
            type="button"
            onClick={() => submit()}
            disabled={!inputValue.trim() || chat.isStreaming}
            aria-label="Send"
            className="hs-press companion-panel__send-button"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 20 20"
              className="companion-panel__svg companion-panel__send-icon"
            >
              <path d="M4 10.5L16 4l-4 12-2.5-4.5z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
